import { useEffect, useState } from "react";
import { Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFreeAccess } from "@/lib/freeAccess";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export const startFounderCheckout = async () => {
  const { data } = await api.post("/payments/checkout", {
    package_id: "founder_monthly", origin_url: window.location.origin,
  });
  window.location.href = data.url;
};

/** One-time nudge when a free account has exactly 2 qualifying activities left. */
export default function UpgradeMoment() {
  const { user } = useAuth();
  const { status } = useFreeAccess(user);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const seenKey = user ? `sr_upgrade_moment_seen:${user.id}` : null;

  useEffect(() => {
    if (status?.subject && !status.blocked && status.remaining_activities === 2
        && seenKey && !localStorage.getItem(seenKey)) {
      localStorage.setItem(seenKey, "1");
      setOpen(true);
    }
  }, [status, seenKey]);

  const upgrade = async () => {
    setBusy(true);
    try {
      await startFounderCheckout();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <Dialog open onOpenChange={setOpen}>
      <DialogContent data-testid="upgrade-moment-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown size={20} className="text-spark-purple" /> Just 2 free activities left!
          </DialogTitle>
          <DialogDescription>
            You're on a roll. Become a Founder member for $6.99/month, with that price locked for
            your first year, and keep learning without limits. Nothing is charged until you confirm at checkout.
          </DialogDescription>
        </DialogHeader>
        {status.founder_seats?.remaining > 0 && (
          <p className="text-sm font-semibold text-spark-purple" data-testid="upgrade-moment-seats">
            {status.founder_seats.remaining} of {status.founder_seats.limit} Founder memberships remaining.
          </p>
        )}
        <DialogFooter className="gap-2">
          <button className="btn-ghost" onClick={() => setOpen(false)} data-testid="upgrade-moment-later-btn">Maybe later</button>
          <button className="btn-primary" onClick={upgrade} disabled={busy} data-testid="upgrade-moment-founder-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Crown size={16} />} Become a Founder — $6.99/mo
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
